/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import CloseIcon from '@mui/icons-material/Close'

import { alternateGray, baseColor } from '../../../../lib/styles/colors'
import { tertiaryBodyCss } from '../../../../lib/styles/typography'
import { IconButton } from '../../buttons'

type BannerProps = {
  /** Color to use for the banner */
  backgroundColor: string
  /** Content to display in the banner */
  children: React.ReactNode
  /** Optional className to apply to the banner */
  className?: string
  /** Indicates if the banner can be dismissed */
  dismissable?: boolean
  /** Optional icon to show on the banner */
  icon?: React.ReactNode
  /** Optional className for the icon to apply additional styles */
  iconClassName?: string
  /** Optional icon color */
  iconColor?: string
  /** Optional className for banner content */
  innerClassName?: string
  /** Callback for dismiss click */
  onDismiss?: () => void
}

const defaultProps: Pick<
  BannerProps,
  | 'className'
  | 'dismissable'
  | 'icon'
  | 'iconClassName'
  | 'iconColor'
  | 'innerClassName'
  | 'onDismiss'
> = {
  className: undefined,
  dismissable: false,
  icon: undefined,
  iconClassName: undefined,
  iconColor: baseColor,
  innerClassName: undefined,
  onDismiss: undefined
}

const dismissButtonCss = css`
  padding: 8px;
  margin-top: -8px;
  margin-bottom: -8px; // compensates for the extra padding this adds
  margin-right: -16px;
`

const Banner = ({
  backgroundColor,
  children,
  className,
  dismissable,
  icon,
  iconClassName,
  iconColor,
  innerClassName,
  onDismiss
}: BannerProps) => {
  const bannerCss = css`
    background: ${backgroundColor};
    display: grid;
    grid-template-columns: 1fr ${dismissable ? '24px' : ''};
    align-items: start;
    justify-content: center;
    justify-items: center;
    padding: 18px;
    box-sizing: border-box;
    border-radius: 4px;
    min-height: 56px;
  `
  const innerContainerCss = css`
    display: grid;
    grid-template-columns: ${icon ? '30px' : ''} 1fr;
    align-self: center;
  `
  const iconContainerCss = css`
    display: flex;
    align-items: flex-start;
    padding-right: 15px;
    color: ${iconColor};
    margin-top: -2px;
  `

  return (
    <div css={bannerCss} className={className} aria-live="polite">
      <div css={innerContainerCss}>
        {!!icon && (
          <div css={iconContainerCss} className={iconClassName}>
            {icon}
          </div>
        )}
        <div css={tertiaryBodyCss} className={innerClassName}>
          {children}
        </div>
      </div>
      {dismissable && (
        <div>
          <IconButton ariaLabel="Dismiss" css={dismissButtonCss} onClick={onDismiss}>
            <CloseIcon fontSize="small" style={{ color: alternateGray }} />
          </IconButton>
        </div>
      )}
    </div>
  )
}

Banner.defaultProps = defaultProps

export default Banner
